/**
 * Hyperswitch Transactions Page
 *
 * Searchable transaction report for payments.payments collection.
 * Supports search, status filter, date range, and pagination.
 */

import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { manifoldAPI } from '../services/api'
import {
  Card, CardContent, CardHeader, CardTitle,
  Button
} from '../components/ui'
import {
  RefreshCw, Loader2, AlertCircle, Search, X, ChevronLeft, ChevronRight,
  CreditCard, CheckCircle, XCircle, Clock, ExternalLink
} from 'lucide-react'

/**
 * The most specific description of why a payment failed.
 *
 * Deliberately does NOT lead with `unified_message`, which is what this used to
 * show. That field is Hyperswitch's five-bucket classification, not a reason —
 * 44% of failed payments carry UE_3000 "Technical issue with PSP", which reads
 * as a processor outage and is almost always an ordinary issuer decline
 * (`do_not_honor`, issuer code D05). Operators chased Hyperswitch incidents that
 * were not happening.
 *
 * Order is most-specific-first:
 *   description        "Do not honor", "The entered card has insufficient funds"
 *   connector reason   the raw connector sentence, verbose but precise
 *   unified_message    the bucket — a last resort, not a first choice
 *
 * `error_details` is absent on ~2% of failures (SERVER_ERRORs that never
 * reached an issuer), which is why the chain ends where it started.
 */
function failureReason(txn) {
  return (
    txn.error_details?.description
    || txn.error_details?.connector_reason
    || txn.error_message
    || txn.unified_message
    || null
  )
}

const STATUS_CONFIG = {
  succeeded: { color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle },
  failed: { color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: XCircle },
  processing: { color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Clock },
  requires_payment_method: { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', icon: CreditCard },
}

function formatCurrency(cents) {
  if (cents === null || cents === undefined) return '-'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(cents / 100)
}

function formatDateTime(dateStr) {
  if (!dateStr) return '-'
  const d = new Date(dateStr)
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || { color: 'bg-muted text-muted-foreground' }
  const Icon = config.icon
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
      {Icon && <Icon className="w-3 h-3" />}
      {status === 'requires_payment_method' ? 'partial' : status}
    </span>
  )
}

export default function HsTransactionsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedRow, setExpandedRow] = useState(null)

  // Read initial state from URL params
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '')
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all')
  const [startDate, setStartDate] = useState(searchParams.get('startDate') || '')
  const [endDate, setEndDate] = useState(searchParams.get('endDate') || '')
  const [page, setPage] = useState(parseInt(searchParams.get('page')) || 1)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const params = { page, limit: 50 }
      if (search) params.search = search
      if (statusFilter !== 'all') params.status = statusFilter
      if (startDate) params.startDate = startDate
      if (endDate) params.endDate = endDate
      const response = await manifoldAPI.getHsTransactions(params)
      if (response.data.success) {
        setData(response.data.data)
      }
    } catch (err) {
      console.error('[HS-TXN] Error:', err)
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [search, statusFilter, startDate, endDate, page])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Sync filters to URL
  useEffect(() => {
    const params = {}
    if (search) params.search = search
    if (statusFilter !== 'all') params.status = statusFilter
    if (startDate) params.startDate = startDate
    if (endDate) params.endDate = endDate
    if (page > 1) params.page = String(page)
    setSearchParams(params, { replace: true })
  }, [search, statusFilter, startDate, endDate, page, setSearchParams])

  const handleSearch = (e) => {
    e.preventDefault()
    setSearch(searchInput)
    setPage(1)
  }

  const clearSearch = () => {
    setSearchInput('')
    setSearch('')
    setPage(1)
  }

  const handleStatusChange = (s) => {
    setStatusFilter(s)
    setPage(1)
  }

  const pagination = data?.pagination || {}
  const transactions = data?.transactions || []
  const statusSummary = data?.status_summary || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Transactions</h1>
          <p className="text-sm text-muted-foreground mt-1">Search and browse Hyperswitch payment records</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status Summary Pills */}
      {statusSummary.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleStatusChange('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              statusFilter === 'all'
                ? 'bg-foreground text-background'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            All ({statusSummary.reduce((s, st) => s + st.count, 0)})
          </button>
          {statusSummary.map(st => (
            <button
              key={st.status}
              onClick={() => handleStatusChange(st.status)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                statusFilter === st.status
                  ? 'bg-foreground text-background'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {st.status === 'requires_payment_method' ? 'pending' : st.status} ({st.count})
            </button>
          ))}
        </div>
      )}

      {/* Search + Date Filters */}
      <Card>
        <CardContent className="py-3 px-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search payment ID, customer, plan, card last4..."
                  className="w-full pl-9 pr-8 py-1.5 text-sm border rounded-md bg-background"
                />
                {searchInput && (
                  <button type="button" onClick={clearSearch} className="absolute right-2 top-1/2 -translate-y-1/2">
                    <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                  </button>
                )}
              </div>
              <Button type="submit" size="sm">Search</Button>
            </form>
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPage(1) }}
                className="px-2 py-1.5 text-sm border rounded-md bg-background"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPage(1) }}
                className="px-2 py-1.5 text-sm border rounded-md bg-background"
              />
              {(startDate || endDate) && (
                <button onClick={() => { setStartDate(''); setEndDate(''); setPage(1) }}>
                  <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && !data && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Results Table */}
      {data && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                {pagination.total} transaction{pagination.total !== 1 ? 's' : ''}
                {loading && <Loader2 className="inline w-4 h-4 ml-2 animate-spin" />}
              </CardTitle>
              {pagination.total_pages > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)}
                    className="h-7"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    {page} / {pagination.total_pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= pagination.total_pages}
                    onClick={() => setPage(p => p + 1)}
                    className="h-7"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No transactions found</p>
            ) : (
              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 font-medium">Date</th>
                      <th className="text-left p-3 font-medium">Status</th>
                      <th className="text-right p-3 font-medium">Amount</th>
                      <th className="text-left p-3 font-medium">Customer</th>
                      <th className="text-left p-3 font-medium">Plan</th>
                      <th className="text-left p-3 font-medium">Card</th>
                      <th className="text-left p-3 font-medium">Gateway</th>
                      <th className="text-left p-3 font-medium">Payment ID</th>
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(txn => {
                      const isExpanded = expandedRow === txn.payment_id
                      return (
                        <TxnRow
                          key={txn.payment_id}
                          txn={txn}
                          isExpanded={isExpanded}
                          onToggle={() => setExpandedRow(isExpanded ? null : txn.payment_id)}
                        />
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
          {/* Bottom pagination */}
          {pagination.total_pages > 1 && (
            <div className="px-6 pb-4 flex justify-center">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                </Button>
                <span className="text-sm text-muted-foreground px-3">
                  Page {page} of {pagination.total_pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pagination.total_pages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  )
}

function TxnRow({ txn, isExpanded, onToggle }) {
  return (
    <>
      <tr
        className={`border-t hover:bg-muted/30 cursor-pointer ${txn.status === 'failed' ? 'bg-red-50/30 dark:bg-red-950/10' : ''}`}
        onClick={onToggle}
      >
        <td className="p-3 text-xs whitespace-nowrap">{formatDateTime(txn.created)}</td>
        <td className="p-3">
          <StatusBadge status={txn.status} />
          {txn.status === 'failed' && failureReason(txn) && (
            <div
              className="text-[11px] text-red-600 dark:text-red-400 mt-1 max-w-[200px] truncate"
              // The full chain in the tooltip: the row is 200px and these
              // truncate, and the raw connector sentence is often the thing
              // worth reading.
              title={[
                failureReason(txn),
                txn.error_details?.standardised_code && `code: ${txn.error_details.standardised_code}`,
                txn.error_details?.issuer_code && `issuer: ${txn.error_details.issuer_code}`,
              ].filter(Boolean).join('\n')}
            >
              {failureReason(txn)}
            </div>
          )}
        </td>
        <td className="p-3 text-right font-mono">{formatCurrency(txn.amount)}</td>
        <td className="p-3">
          {txn.customer_name ? (
            <div>
              <div className="font-medium text-sm">{txn.customer_name}</div>
              {txn.customer_email && <div className="text-xs text-muted-foreground">{txn.customer_email}</div>}
            </div>
          ) : (
            <span className="text-xs text-muted-foreground font-mono">{txn.customer_id || '-'}</span>
          )}
        </td>
        <td className="p-3 font-mono text-xs">{txn.entity_id || '-'}</td>
        <td className="p-3 text-xs">
          {txn.card_last4 ? (
            <span>{txn.card_network || ''} ****{txn.card_last4}</span>
          ) : '-'}
        </td>
        <td className="p-3 text-xs">
          {txn.connector ? (
            // The MID under the gateway name: same column, because they are
            // one fact — which merchant account at which processor.
            <span className="whitespace-nowrap">
              {txn.connector_label || txn.connector}
              {txn.merchant_connector_id && (
                <span className="block font-mono text-[10px] text-muted-foreground">
                  {txn.merchant_connector_id}
                </span>
              )}
            </span>
          ) : (
            // Never attempted, so no connector was ever chosen. Not a gap.
            <span className="text-muted-foreground">-</span>
          )}
        </td>
        <td className="p-3 font-mono text-xs text-muted-foreground">{txn.payment_id}</td>
        <td className="p-3">
          {txn.customer_id && (
            <a
              href={`https://live.hyperswitch.io/dashboard/customers/${txn.customer_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 whitespace-nowrap"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="w-3 h-3" /> HS
            </a>
          )}
        </td>
      </tr>
      {isExpanded && (
        <tr className="border-t bg-muted/10">
          <td colSpan={9} className="p-4">
            <TxnDetail txn={txn} />
          </td>
        </tr>
      )}
    </>
  )
}

function TxnDetail({ txn }) {
  const fields = [
    ['Payment ID', txn.payment_id],
    ['Status', txn.status],
    ['Amount', formatCurrency(txn.amount)],
    ['Currency', txn.currency],
    ['Gateway', txn.connector_label || txn.connector],
    // The merchant account the charge ran on. One per tenant today, so a change
    // here is a routing change worth noticing.
    ['MID', txn.merchant_connector_id],
    ['Processor Txn ID', txn.connector_transaction_id],
    ['Created', formatDateTime(txn.created)],
    ['Customer ID', txn.customer_id],
    ['Customer', txn.customer_name],
    ['Email', txn.customer_email],
    ['Plan / Entity', txn.entity_id],
    ['Subscription ID', txn.subscription_id],
    ['Invoice ID', txn.invoice_id],
    ['Card Network', txn.card_network],
    ['Card Type', txn.card_type],
    ['Card Last 4', txn.card_last4],
    ['Card Issuer', txn.card_issuer],
    ['Card Country', txn.card_country],
    ['Attempts', txn.attempt_count],
  ]

  // Ordered most useful first. The unified pair is last on purpose: it is the
  // bucket Hyperswitch sorted the failure into, and it is the field that sent
  // people looking for a processor outage behind an ordinary decline.
  const errorFields = txn.error_code ? [
    ['Reason', txn.error_details?.description],
    ['Standardised Code', txn.error_details?.standardised_code],
    ['Issuer Code', txn.error_details?.issuer_code],
    ['Network', txn.error_details?.network],
    ['Connector Code', txn.error_details?.connector_code || txn.error_code],
    ['Connector Reason', txn.error_details?.connector_reason || txn.error_message],
    ['Recommended Action', txn.error_details?.recommended_action],
    ['Hyperswitch Bucket', txn.unified_code && `${txn.unified_code} — ${txn.unified_message}`],
  ] : []

  // What a support agent can tell the customer, in Hyperswitch's own words.
  const customerGuidance = txn.error_details?.guidance

  return (
    <div className="space-y-4">
      {txn.customer_id && (
        <a
          href={`https://live.hyperswitch.io/dashboard/customers/${txn.customer_id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
        >
          <ExternalLink className="w-3.5 h-3.5" /> Open in Hyperswitch Dashboard
        </a>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2">
        {fields.map(([label, value]) => (
          value ? (
            <div key={label}>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</div>
              <div className="text-xs font-mono break-all">{value}</div>
            </div>
          ) : null
        ))}
      </div>

      {errorFields.length > 0 && (
        <div className="border border-red-200 dark:border-red-900/30 rounded-lg p-3 bg-red-50/50 dark:bg-red-950/10">
          <div className="text-xs font-semibold text-red-700 dark:text-red-400 mb-2">Error Details</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
            {errorFields.map(([label, value]) => (
              value ? (
                <div key={label}>
                  <div className="text-[10px] text-red-500 uppercase tracking-wide">{label}</div>
                  <div className="text-xs font-mono break-all">{value}</div>
                </div>
              ) : null
            ))}
          </div>

          {/* Hyperswitch's own customer-facing wording. Support is otherwise
              left paraphrasing a decline code at the patient. */}
          {customerGuidance && (
            <div className="mt-3 pt-3 border-t border-red-200 dark:border-red-900/30">
              <div className="text-[10px] text-red-500 uppercase tracking-wide">What to tell the customer</div>
              <div className="text-xs mt-0.5">{customerGuidance}</div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
